import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.css';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ToastProvider } from './components/Toast';

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event, hint) {
      if (event.exception) {
        const error = hint.originalException;
        if (error && typeof error === 'object' && 'message' in error) {
          const message = String(error.message).toLowerCase();
          if (
            message.includes('network error') ||
            message.includes('failed to fetch') ||
            message.includes('load failed')
          ) {
            return null;
          }
        }
      }
      return event;
    },
  });
}

const pathname = window.location.pathname;
const isAssessmentRoute = pathname === '/assessment' || pathname === '/assessment/thank-you';

const renderError = (err: unknown) => {
  const root = document.getElementById('root');
  if (root) {
    root.innerHTML = `<div style="min-height:100vh;display:flex;align-items:center;justify-content:center;padding:2rem;font-family:system-ui,sans-serif"><div style="max-width:28rem;text-align:center"><h1 style="font-size:1.5rem;font-weight:700;color:#111;margin-bottom:1rem">Something went wrong</h1><p style="color:#666;margin-bottom:1.5rem">We're having trouble loading this page. Please try refreshing.</p><button onclick="location.reload()" style="background:#2563eb;color:#fff;padding:0.75rem 1.5rem;border:none;border-radius:0.5rem;font-size:1rem;cursor:pointer">Refresh Page</button></div></div>`;
  }
  console.error('Failed to load application:', err);
};

if (isAssessmentRoute) {
  import('./components/AssessmentPage').then(({ AssessmentPage }) => {
    createRoot(document.getElementById('root')!).render(
      <StrictMode>
        <ErrorBoundary>
          <ToastProvider>
            <AssessmentPage />
          </ToastProvider>
        </ErrorBoundary>
      </StrictMode>
    );
  }).catch(renderError);
} else {
  import('./App').then(({ default: App }) => {
    createRoot(document.getElementById('root')!).render(
      <StrictMode>
        <ErrorBoundary>
          <ToastProvider>
            <App />
          </ToastProvider>
        </ErrorBoundary>
      </StrictMode>
    );
  }).catch(renderError);
}
